import {
  NAME_EDITION,
  NAME_FIRST_YEAR,
  NAME_LAST_YEAR,
  type NameSummary,
  type YearsOverview,
} from "@listou/shared/schemas/prenoms";
import { Link } from "@tanstack/react-router";
import { ArrowRight, CalendarDays, MapPin, TrendingUp, Trophy } from "lucide-react";
import { useState } from "react";

import { nameCards } from "../../lib/api";
import { BOYS, GIRLS, normalize, number } from "./format";
import { card, NameFinder, NameLink, NamesLayout, PrenomsNav, Source, YearLink } from "./layout";
import { ShareButton } from "./share";
import { breadcrumbJsonLd, datasetJsonLd, graph } from "./structured-data";

export const namesIndexHead = (count: number) => ({
  title: "Prénoms en France : statistiques, classements et cartes · Listou",
  image: nameCards.page("index", "og"),
  description: `Tapez un prénom et découvrez sa popularité depuis ${NAME_FIRST_YEAR}, son classement, son évolution et sa carte par département. ${number(count)} prénoms, données Insee ${NAME_LAST_YEAR}.`,
  jsonLd: graph(
    breadcrumbJsonLd([{ label: "Prénoms" }], "/prenoms"),
    datasetJsonLd({
      name: `Prénoms donnés en France, ${NAME_FIRST_YEAR}–${NAME_LAST_YEAR}`,
      description: "Naissances par prénom, année et département, filles et garçons.",
      coverage: `${NAME_FIRST_YEAR}/${NAME_LAST_YEAR}`,
    }),
  ),
});

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export function NamesIndex({ names, years }: { names: NameSummary[]; years: YearsOverview }) {
  const [sex, setSex] = useState<"all" | "girls" | "boys">("all");
  const [letter, setLetter] = useState<string | null>(null);
  const [limit, setLimit] = useState(60);
  const latest = years.at(-1);
  const filtered = names.filter(
    (name) =>
      (letter === null || normalize(name.name).startsWith(letter.toLowerCase())) &&
      (sex === "all" || (sex === "girls" ? name.girls >= name.boys : name.boys > name.girls)),
  );
  const filter = (apply: () => void) => {
    apply();
    setLimit(60);
  };
  return (
    <NamesLayout>
      <header className="space-y-6">
        <PrenomsNav
          current="/prenoms"
          share={
            <ShareButton
              path="/prenoms"
              title="L’atlas des prénoms"
              text="Quelle est l’histoire de votre prénom ? Sur Listou :"
              card={(format) => nameCards.page("index", format)}
              slug="atlas-des-prenoms"
            />
          }
        />
        <h1 className="max-w-4xl text-4xl sm:text-6xl">
          Un prénom, toute son histoire depuis {NAME_FIRST_YEAR}
        </h1>
        <p className="max-w-2xl text-lg text-muted">
          Combien de bébés ont reçu votre prénom ? Quand a-t-il été à la mode ? Dans quels
          départements l’a-t-on le plus donné ? {number(names.length)} prénoms, de {NAME_FIRST_YEAR}{" "}
          à {NAME_LAST_YEAR}, d’après l’Insee (édition {NAME_EDITION}).
        </p>
      </header>
      <section className="rounded-3xl bg-ink p-6 text-white sm:p-9">
        <h2 className="mb-4 text-2xl font-bold">Découvrez les stats de votre prénom</h2>
        <NameFinder names={names} large />
        <p className="mt-4 text-sm text-white/70">
          Ajoutez votre année de naissance pour savoir quel rang votre prénom occupait cette
          année-là.
        </p>
      </section>
      <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Link to="/prenoms/annee" className={`${card} block transition hover:border-ink`}>
          <p className="flex items-center gap-2 text-sm font-bold text-muted">
            <CalendarDays size={16} aria-hidden="true" />
            Par année
          </p>
          <p className="mt-2 text-2xl font-bold">Le top de chaque année</p>
          <p className="mt-2 text-muted">
            Le prénom n° 1 de {NAME_FIRST_YEAR} à {NAME_LAST_YEAR}, avec le classement complet.
          </p>
        </Link>
        <Link to="/prenoms/departement" className={`${card} block transition hover:border-ink`}>
          <p className="flex items-center gap-2 text-sm font-bold text-muted">
            <MapPin size={16} aria-hidden="true" />
            Par département
          </p>
          <p className="mt-2 text-2xl font-bold">Les prénoms près de chez vous</p>
          <p className="mt-2 text-muted">Une carte et un classement pour les 101 départements.</p>
        </Link>
        <Link to="/prenoms/tendances" className={`${card} block transition hover:border-ink`}>
          <p className="flex items-center gap-2 text-sm font-bold text-muted">
            <TrendingUp size={16} aria-hidden="true" />
            Tendances
          </p>
          <p className="mt-2 text-2xl font-bold">Ce qui monte, ce qui s’éteint</p>
          <p className="mt-2 text-muted">Hausses, baisses, nouveaux prénoms et prénoms rares.</p>
        </Link>
        {latest && (
          <div className={`${card} space-y-2`}>
            <p className="flex items-center gap-2 text-sm font-bold text-muted">
              <Trophy size={16} aria-hidden="true" />
              Le top {latest.year}
            </p>
            <p className="text-2xl font-bold">
              {latest.girl && (
                <NameLink slug={latest.girl.slug} className="underline decoration-2">
                  <span style={{ color: GIRLS }}>{latest.girl.name}</span>
                </NameLink>
              )}{" "}
              et{" "}
              {latest.boy && (
                <NameLink slug={latest.boy.slug} className="underline decoration-2">
                  <span style={{ color: BOYS }}>{latest.boy.name}</span>
                </NameLink>
              )}
            </p>
            <YearLink year={latest.year} className="inline-flex items-center gap-1 underline">
              Voir le classement {latest.year}
              <ArrowRight size={16} aria-hidden="true" />
            </YearLink>
          </div>
        )}
      </section>
      <section className="space-y-5">
        <h2 className="text-3xl font-bold">Parcourir les prénoms</h2>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Sexe">
          {(
            [
              ["all", "Tous"],
              ["girls", "Majoritairement féminins"],
              ["boys", "Majoritairement masculins"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={sex === value}
              onClick={() => filter(() => setSex(value))}
              className={`rounded-full border px-4 py-2 text-sm font-bold ${sex === value ? "border-ink bg-ink text-white" : "border-line"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Initiale">
          <button
            type="button"
            aria-pressed={letter === null}
            onClick={() => filter(() => setLetter(null))}
            className={`rounded-lg px-3 py-1 text-sm font-bold ${letter === null ? "bg-ink text-white" : "border border-line"}`}
          >
            A–Z
          </button>
          {LETTERS.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={letter === value}
              onClick={() => filter(() => setLetter(value))}
              className={`w-8 rounded-lg py-1 text-sm font-bold ${letter === value ? "bg-ink text-white" : "border border-line"}`}
            >
              {value}
            </button>
          ))}
        </div>
        <p className="text-muted">
          {number(filtered.length)} prénoms, classés par nombre de naissances depuis{" "}
          {NAME_FIRST_YEAR}. Seuls les prénoms donnés au moins 100 fois figurent ici.
        </p>
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.slice(0, limit).map((name) => {
            const girls = (name.girls / name.total) * 100;
            return (
              <li key={name.slug}>
                <NameLink
                  slug={name.slug}
                  className={`${card} block p-5! transition hover:-translate-y-0.5 hover:border-ink`}
                >
                  <span className="block font-display text-2xl font-bold">{name.name}</span>
                  <span className="mt-1 block text-sm text-muted">
                    {number(name.total)} naissances
                  </span>
                  <span
                    className="mt-3 flex h-1.5 overflow-hidden rounded-full"
                    role="img"
                    aria-label={`${Math.round(girls)} % de filles`}
                  >
                    <span style={{ width: `${girls}%`, background: GIRLS }} />
                    <span style={{ flex: 1, background: BOYS }} />
                  </span>
                </NameLink>
              </li>
            );
          })}
        </ul>
        {filtered.length === 0 && <p>Aucun prénom ne correspond à ce filtre.</p>}
        {limit < filtered.length && (
          <button type="button" className="btn" onClick={() => setLimit(limit + 60)}>
            Voir 60 prénoms de plus
          </button>
        )}
      </section>
      <Source />
    </NamesLayout>
  );
}
